<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
    <!--Customers - Medium and large screens-->
    <div class="row mt-3 overflow-scroll">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <div class="row justify-content-between mx-0">
            <h4 class="card-title">Customers</h4>
          </div>
          <filter-bar
            ref="filterBar"
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="players"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          >
            <template #actions>
              <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" v-on:click="downloadCSV()">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>
          <info-hint
            v-if="hasPermissions('customer_archive') && !filterValues.status && archivedReturnedCount > 0"
            variant="warning"
            icon="icon-alert-circle-exc"
          >
            {{ archivedReturnedCount === 1 ? '1 archived customer visited again after being archived.' : archivedReturnedCount + ' archived customers visited again after being archived.' }}
            <template #action>
              <base-button size="sm" variant="outlined" tone="primary" @click="showArchivedReturned">Show them</base-button>
            </template>
          </info-hint>
          <!--   no data panel -->
          <div v-if="empty==true" class="mt-3" >
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No customers to display.</h4>
            </div>
          </div>

          <!--        Mobile-->
          <div class="row d-sm-none d-block">
            <div class="col">
              <b-table v-if="empty==false" striped hover :items="customers"
                       :fields="['customers']">
                <template v-slot:head(customers)="row">
                  <span>Total results: {{totalRows}}</span>
                </template>
                <template v-slot:cell(customers)="row">
                  <div class="row">
                    <div class="col">
                      <p class="text-white">{{row.item.first_name}} {{row.item.last_name}}
                        <router-link
                          :to="'/customers/details?id=' + row.item.end_user_id"
                          custom
                          v-slot="{ href }"
                        >
                          <a :href="href" target="_blank" rel="noopener">
                            <i class="tim-icons el-icon-top-right"></i>
                          </a>
                        </router-link>
                        &nbsp;<badge v-if="row.item.is_minor" type="primary">Minor</badge>
                        &nbsp;<badge v-if="row.item.visited_again" type="warning">Visited again</badge>
                      </p>
                      <p v-if="row.item.birthday && !row.item.birthday.includes('000')" class="text-warning">
                        <i class="tim-icons icon-gift-2"></i> {{getDate(row.item.birthday)}}
                      </p>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <small v-if="row.item.email">
                        <i class="tim-icons icon-email-85"></i> {{row.item.email}}
                      </small>
                      <small v-if="row.item.phone">
                        <i class="tim-icons icon-mobile"></i>
                        {{ formatPhone(row.item.country, row.item.phone) }}
                      </small>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col">
                      <label>Date joined</label>
                      <p>
                        <span v-if="!row.item.date_joined.includes('0000-00-00')"> {{getLocalDate(row.item.date_joined)}}</span>
                        <span v-else>-</span>
                      </p>
                    </div>
                    <div class="col">
                      <label>Date last visited</label>
                      <p>
                        <span v-if="!row.item.date_last_visited.includes('0000-00-00')"> {{getLocalDate(row.item.date_last_visited)}}</span>
                        <span v-else>-</span>
                      </p>
                    </div>
                    <div v-if="filterValues.status" class="col">
                      <label>Date archived</label>
                      <p>
                        <span v-if="row.item.archived_at">{{getLocalDate(row.item.archived_at)}}</span>
                        <span v-else>-</span>
                      </p>
                    </div>

                  </div>
                  <div class="row mt-2">
                    <div class="col">
                      <el-dropdown>
                        <base-button
                          simple
                          type="primary"
                          size="sm"
                        >
                          Actions <i class="ml-1 tim-icons icon-minimal-down"></i>
                        </base-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <a class="dropdown-item" target="_blank" :href="'/customers/details?id=' + row.item.end_user_id">
                              <i class="tim-icons text-default el-icon-user mr-2"></i> View customer details
                            </a>
                            <template v-if="hasPermissions('customer_archive')">
                              <hr>
                              <a v-if="Number(row.item.is_archived) === 1" class="dropdown-item" @click="openRestoreDialog(row.item.id)">
                                <i class="tim-icons text-default icon-refresh-02 mr-2"></i> Restore customer
                              </a>
                              <a v-else class="dropdown-item" @click="openArchiveDialog(row.item.id)">
                                <i class="tim-icons text-default el-icon-delete mr-2"></i> Archive customer
                              </a>
                            </template>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>
          </div>

          <!--        Large screens-->
          <div class="row d-sm-block d-none">
            <div class="col">
              <b-table v-if="empty==false" striped hover :items="customers"
                       :fields="fields">
                <template v-slot:cell(name)="row">
                  <p class="text-white">{{row.item.first_name}} {{row.item.last_name}}
                    &nbsp;<badge v-if="row.item.is_minor" type="primary">Minor</badge>
                    &nbsp;<badge v-if="row.item.visited_again" type="warning">Visited again</badge>
                  </p>
                  <p v-if="row.item.birthday && !row.item.birthday.includes('000')" class="text-warning">
                    <i class="tim-icons icon-gift-2"></i> {{getDate(row.item.birthday)}}
                  </p>
                  <p v-if="!row.item.first_name && !row.item.last_name">-</p>
                </template>
                <template v-slot:cell(contact_information)="row">
                  <p v-if="!row.item.phone && !row.item.email">-</p>
                  <p v-if="row.item.email" class="text-white">
                    <i class="tim-icons icon-email-85"></i> {{row.item.email}}
                  </p>
                  <p v-if="row.item.phone" class="text-white">
                    <i class="tim-icons icon-mobile"></i>
                    {{ formatPhone(row.item.country, row.item.phone) }}
                  </p>
                </template>
                <template v-slot:cell(date_joined)="row">
                  <span v-if="!row.item.date_joined.includes('0000-00-00')"> {{getLocalDate(row.item.date_joined)}}</span>
                  <span v-else>-</span>
                </template>
                <template v-slot:cell(last_visited)="row">
                  <span v-if="!row.item.date_last_visited.includes('0000-00-00')"> {{getLocalDate(row.item.date_last_visited)}}</span>
                  <span v-else>-</span>
                </template>
                <template v-slot:cell(archived_at)="row">
                  <span v-if="row.item.archived_at">{{getLocalDate(row.item.archived_at)}}</span>
                  <span v-else>-</span>
                </template>
                <template v-slot:cell(events)="row">
                  <el-popover
                    v-if="formatJSON(row.item.games).length > 0"
                    trigger="hover"
                    placement="bottom"
                    popper-class="otc-pop otc-pop--panel"
                  >
                    <div>
                      <p v-for="(g, i) in formatJSON(row.item.games)" :key="'G' + i" class="mb-1">{{g.game_name}}</p>
                    </div>
                    <badge slot="reference" type="primary">{{formatJSON(row.item.games).length}}</badge>
                  </el-popover>
                  <span v-else>0</span>
                </template>
                <template v-slot:cell(groups)="row">
                  <span v-for="(g, i) in formatJSON(row.item.groups)" :key="'GR' + i">
                    <badge type="default">{{g.group_name}}</badge>&nbsp;
                  </span>
                  <span v-if="formatJSON(row.item.groups).length === 0">-</span>
                </template>
                <template v-slot:cell(tags)="row">
                  <div v-if="formatJSON(row.item.tags).length > 0" class="tags-row">
                    <span
                      v-for="(t, i) in formatJSON(row.item.tags)"
                      :key="'T' + i"
                      class="tag-chip text-white"
                      :style="tagChipStyle(t.color)"
                    >
                      {{t.tag_name}}
                    </span>
                  </div>
                  <span v-else>-</span>
                </template>

                <template v-slot:cell(actions)="row">
                  <el-dropdown>
                    <base-button
                      simple
                      type="primary"
                      link
                    > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item" target="_blank" :href="'/customers/details?id=' + row.item.end_user_id">
                          <i class="tim-icons text-default el-icon-user mr-2"></i> View customer details
                        </a>
                        <template v-if="hasPermissions('customer_archive')">
                          <hr>
                          <a v-if="Number(row.item.is_archived) === 1" class="dropdown-item" @click="openRestoreDialog(row.item.id)">
                            <i class="tim-icons text-default icon-refresh-02 mr-2"></i> Restore customer
                          </a>
                          <a v-else class="dropdown-item" @click="openArchiveDialog(row.item.id)">
                            <i class="tim-icons text-default el-icon-delete mr-2"></i> Archive customer
                          </a>
                        </template>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
            </div>
          </div>
          <b-pagination v-on:input="getCustomers(perPage, getOffset())"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
          ></b-pagination>
        </card>
      </div>
    </div>


    <base-alert-dialog
      :show="archiveDialog.show"
      title="Archive customer"
      variant="danger"
      message="This customer will be hidden from your customer list. You can restore them from the archived status filter."
      show-cancel
      confirm-text="Archive"
      confirm-variant="danger"
      :loading="archiveDialog.loading"
      :dismissible="!archiveDialog.loading"
      @confirm="archiveCustomer"
      @cancel="archiveDialog.show = false" />
    <base-alert-dialog
      :show="restoreDialog.show"
      title="Restore customer"
      variant="primary"
      message="This customer will return to your customer list."
      show-cancel
      confirm-text="Restore"
      :loading="restoreDialog.loading"
      :dismissible="!restoreDialog.loading"
      @confirm="restoreCustomer"
      @cancel="restoreDialog.show = false" />
  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import { tagChipStyle } from "@/util/tagChip";
import axios from "axios";
import {BTable, BPagination, BIcon, BIconFilter, BIconArrowCounterclockwise} from 'bootstrap-vue'
import Badge from "@/components/Badge";
import {dayOfWeek, formattedDate, time} from '../../plugins/dateFormatter'
import {formatGroupDate} from '@/plugins/dates'
import swal from "sweetalert2";
import _ from "lodash";
import {Dropdown, DropdownItem, DropdownMenu, Popover} from "element-ui";
import {formatPhoneDisplay} from "@/util/phoneFormatter";


export default {
  name: "Players",
  components: {
    Badge,
    BTable,
    BPagination,
    BIcon,
    BIconFilter,
    BIconArrowCounterclockwise,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Popover.name]: Popover
  },
  data() {
    return {
      loading: false,
      empty: false,
      customers: [],
      gameOptions: '',
      groupOptions: [],
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      perPageOptions: [5, 10, 25, 50],
      perPage: 10,
      // SavedViewsMenu drives the first fetch via @ready; this guards it.
      didInitialFetch: false,
      currentPage: 1,
      totalRows: 0,
      // Flat filter values for the shared <filter-bar>. Seeded from the URL;
      // saved-views precedence is resolved by FilterBar. game = game id.
      archivedReturnedCount: 0,
      archiveDialog: { show: false, customerId: null, loading: false },
      restoreDialog: { show: false, customerId: null, loading: false },
      filterValues: {
        status: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        game: '',
        tag: '',
        group: '',
        eventCount: { from: '', to: '' }
      }
    }
  },
  computed: {
    // Groups column only earns its place for multi-group companies.
    fields() {
      const base = ['name', 'contact_information', 'date_joined', 'last_visited', { key: 'events', label: 'Events' }, 'tags', 'actions']
      if (this.groupOptions.length > 1) {
        base.splice(5, 0, { key: 'groups', label: 'Groups' })
      }
      // Archived lists show when each customer was archived, after last visited
      if (this.filterValues.status) {
        base.splice(4, 0, { key: 'archived_at', label: 'Archived' })
      }
      return base
    },
    // Schema for the shared <filter-bar>: text fields + a single-select game.
    filterSchema() {
      return [
        { key: 'firstName', type: 'text', label: 'First name', placeholder: 'First name', operatorLabel: 'contains', primary: true },
        { key: 'lastName', type: 'text', label: 'Last name', placeholder: 'Last name', operatorLabel: 'contains', primary: true },
        { key: 'email', type: 'text', label: 'Email', placeholder: 'Email', operatorLabel: 'contains', primary: true },
        { key: 'phone', type: 'text', label: 'Phone number', placeholder: 'Phone', operatorLabel: 'contains', primary: true },
        { key: 'game', type: 'select', label: 'Event', primary: true,
          options: (Array.isArray(this.gameOptions) ? this.gameOptions : []).map(g => ({ label: g.name, value: g.id })) },
        { key: 'eventCount', type: 'numberrange', label: 'Event count', primary: true },
        { key: 'tag', type: 'text', label: 'Tag', placeholder: 'Tag', operatorLabel: 'contains', primary: true },
        ...(this.groupOptions.length > 1 ? [{ key: 'group', type: 'select', label: 'Group', primary: true,
          options: this.groupOptions.map(g => ({ label: g.name, value: g.id })) }] : []),
        // Empty means active customers; either option switches the list to archived ones
        { key: 'status', type: 'select', label: 'Archived', primary: true,
          options: [{ label: 'All', value: 'archived' }, { label: 'Visited again', value: 'archived_returned' }] }
      ]
    }
  },
  methods: {
    tagChipStyle,
    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values){
      this.filterValues = {
        status: values.status || '',
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        email: values.email || '',
        phone: values.phone || '',
        game: values.game || '',
        tag: values.tag || '',
        group: values.group || '',
        eventCount: { ...(values.eventCount || { from: '', to: '' }) }
      }
      if(!this.didInitialFetch){
        this.didInitialFetch = true
        this.getCustomers(this.perPage, this.getOffset())
      } else {
        // Applying a filter/view lands on page 1; reset before computing the offset
        // (getCustomers resets currentPage only after the offset arg is built).
        this.currentPage = 1
        this.getCustomers(this.perPage, this.getOffset(), 'filter')
      }
    },
    onPerPageChange(n){
      this.perPage = n
      this.currentPage = 1
      this.getCustomers(this.perPage, this.getOffset(), 'filter')
    },
    formatPhone(country, phone) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getDayOfWeek(d){
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    getFormattedDate(d){
      if(d instanceof Date){
        d = d.getFullYear() + '-' + (d.getMonth()+1) + '-' + d.getDate()
      }
      return formattedDate(this.date_format, d)
    },
    formatJSON(string){
      // Null-safe: rows without tags/games arrive as null from the API
      const parsed = string ? JSON.parse(string) : null
      return parsed || []
    },
    getOffset(){
      return Number((this.currentPage * this.perPage) - this.perPage)
    },
    getDate(d){
      return formattedDate(d)
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getCustomers(count, offset, source){
      if(source === 'filter'){
        this.currentPage = 1
      }
      // FilterBar flat values: text fields + game id.
      const f = this.filterValues
      const game = f.game || ''
      const eventCount = f.eventCount || {}

      this.loading = true
      const token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/customers?count=' + count + '&offset=' + offset +
        "&first_name=" + encodeURIComponent(f.firstName) +  "&last_name=" + encodeURIComponent(f.lastName)
        +  "&email=" + encodeURIComponent(f.email) +  "&phone=" + encodeURIComponent(f.phone) + "&game=" + encodeURIComponent(game) + "&tag=" + encodeURIComponent(f.tag)
        + "&group=" + encodeURIComponent(f.group || '')
        + "&event_count_from=" + encodeURIComponent(eventCount.from || '')
        + "&event_count_to=" + encodeURIComponent(eventCount.to || '')
        + "&status=" + encodeURIComponent(f.status || '')
      axios.get(url, config)
        .then(response => {

          this.loading = false
          this.customers = response.data.customers
          this.totalRows = response.data.totalRows
          this.archivedReturnedCount = response.data.archivedReturnedCount || 0
          if(this.customers.length <= 0){
            this.empty = true
          } else {
            this.empty = false
          }
          this.addQueryParameters()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    async getGames () {
      this.loading = true
      var token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.gameOptions = response.data
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }``
        })
    },
    async getGroupOptions () {
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const companyId = localStorage.getItem('companyId')
      const url = API_LOCATION + 'companies/' + companyId + '/groups'
      axios.get(url, config)
        .then(response => {
          this.groupOptions = response.data
        })
        .catch(err => {
          console.log(err)
        })
    },
    async getAllCustomers(config, count){
      let url = ''
      let promises = []
      let csvData = []
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id

      let offset = 0
      while(offset <= this.totalRows){

        url = API_LOCATION + 'companies/' + companyId  + '/groups/' + companyGroupId +
          '/customers?count=' + count + "&offset=" + offset + "&game="
          + "&status=" + encodeURIComponent(this.filterValues.status || '')
        promises.push(
          axios.get(url, config)
            .then(response =>{
              csvData.push(response.data.customers)
            })
            .catch(err => {
                console.log(err)
                if (err.response.status == 401) {
                  swal({
                    title: 'Error',
                    html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                    type: 'error',
                  }).then((response)=> {
                    this.$router.push('/')
                  })
                } else {
                  swal('Error!', 'Something went wrong', 'error')
                }
              }
            )
        )
        offset = offset + count
      }

      await Promise.all(promises)

      // merge all response arrays into one
      let arr = []
      csvData.forEach((element) => {
        arr = arr.concat(element)

      })
      return arr
    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        const needsQuoting = value.includes(',') || value.includes('\n') || value.includes('"');
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        if (needsQuoting) {
          value = `"${value}"`;
        }
      }
      return value;
    },
    // Empty-safe date for CSV cells, in the group's configured date format.
    csvDate(d){
      if(!d || String(d).includes('0000-00-00')) return ''
      return formatGroupDate(d)
    },
    // Flatten a tags/games/groups JSON column to a "; "-joined name list.
    csvNames(json, key){
      return this.formatJSON(json).map(x => x[key]).filter(Boolean).join('; ')
    },
    async downloadCSV(){
      //get the entire data set
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      this.loading = true
      const count = 5000
      const csvData = await this.getAllCustomers(config, count)
      this.loading = false

      // Archived exports add the archive date after last visited
      const archived = Boolean(this.filterValues.status)
      let csv = 'First name,Last name,Birthday,Email,Phone,Address line 1,Address line 2,City,State,Zip,Country,Date joined,Date last visited,'
        + (archived ? 'Date archived,' : '')
        + 'Events,Groups,Tags,Opted out of emails,Opted out of photo sharing\n';
      csvData.forEach(row => {
        // Raw cell values only; escaping is applied uniformly below so no
        // column can ever ship unescaped.
        const cells = [
          row.first_name,
          row.last_name,
          this.csvDate(row.birthday),
          row.email,
          row.phone,
          row.address_line_1,
          row.address_line_2,
          row.city,
          row.state,
          row.zip,
          row.country,
          this.csvDate(row.date_joined),
          this.csvDate(row.date_last_visited),
          ...(archived ? [this.csvDate(row.archived_at)] : []),
          this.csvNames(row.games, 'game_name'),
          this.csvNames(row.groups, 'group_name'),
          this.csvNames(row.tags, 'tag_name'),
          row.opted_out_email ? 'Yes' : 'No',
          row.opted_out_photo_sharing ? 'Yes' : 'No'
        ]
        csv += cells.map(v => this.escapeCSVValue(v)).join(',')
        csv += "\n";
      });

      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = this.filterValues.status ? 'archived-customers.csv' : 'customers.csv';
      anchor.click();
    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        first_name: this.filterValues.firstName,
        last_name: this.filterValues.lastName,
        email: this.filterValues.email,
        phone: this.filterValues.phone,
        game: this.filterValues.game || '',
        tag: this.filterValues.tag,
        status: this.filterValues.status || '',
        group: this.filterValues.group || '',
        event_count_from: (this.filterValues.eventCount || {}).from || '',
        event_count_to: (this.filterValues.eventCount || {}).to || '',
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    showArchivedReturned() {
      const values = { status: 'archived_returned' }
      this.$refs.filterBar.applyValues(values)
      this.onFiltersChanged(values)
    },
    openArchiveDialog(customerId) {
      this.archiveDialog = { show: true, customerId, loading: false }
    },
    openRestoreDialog(customerId) {
      this.restoreDialog = { show: true, customerId, loading: false }
    },
    async archiveCustomer() {
      await this.changeArchiveState(this.archiveDialog, 'delete', '', 'Customer archived')
    },
    async restoreCustomer() {
      await this.changeArchiveState(this.restoreDialog, 'put', '/restore', 'Customer restored')
    },
    async changeArchiveState(dialog, method, suffix, successMessage) {
      dialog.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id
        const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/customers/${dialog.customerId}${suffix}`
        if (method === 'put') {
          await axios.put(url, {}, config)
        } else {
          await axios.delete(url, config)
        }
        dialog.show = false
        this.$notify({ type: 'success', message: successMessage, icon: 'tim-icons icon-check-2' })
        this.getCustomers(this.perPage, this.getOffset())
      } catch (err) {
        console.log(err)
        const message = err.response && err.response.status === 401
          ? 'You do not have access to this customer.'
          : 'Something went wrong. Please try again.'
        this.$notify({ type: 'danger', message, icon: 'tim-icons icon-alert-circle-exc' })
      } finally {
        dialog.loading = false
      }
    }
  },
  mounted() {
    this.getGames()
    this.getGroupOptions()
    // Default when the query omits count/page (e.g. arriving from a saved view with
    // only ?view=), otherwise Number(undefined) => NaN builds a bad fetch URL.
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    this.loading = true
    // Seed FilterBar's flat values from the URL (game = id). FilterBar resolves
    // saved-views precedence (?view= > URL > default > last-used) and drives the
    // first fetch via its initial `change` (onFiltersChanged).
    const q = this.$route.query || {}
    this.filterValues = {
      status: q.status || '',
      firstName: q.first_name || '',
      lastName: q.last_name || '',
      email: q.email || '',
      phone: q.phone || '',
      game: q.game ? Number(q.game) : '',
      tag: q.tag || '',
      group: q.group ? Number(q.group) : '',
      eventCount: { from: q.event_count_from || '', to: q.event_count_to || '' }
    }
  }
}
</script>

<style scoped>

</style>
